'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2, Wrench } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Shelf } from '../types';

interface ShelfCardProps {
  shelf: Shelf;
  onEdit: (shelf: Shelf) => void;
  onDelete: (shelf: Shelf) => void;
  onMarkMaintenance: (shelf: Shelf) => void;
}

function getCapacityBarColor(
  utilization: number,
  status: Shelf['status']
): string {
  if (status === 'maintenance') return 'bg-muted-foreground';
  if (utilization >= 100) return 'bg-red-500';
  if (utilization >= 90) return 'bg-orange-500';
  return 'bg-blue-500';
}

export function ShelfCard({
  shelf,
  onEdit,
  onDelete,
  onMarkMaintenance
}: ShelfCardProps) {
  const t = useTranslations('shelves');

  const statusVariants: Record<
    Shelf['status'],
    { badge: string; dot: string }
  > = {
    active: {
      badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
      dot: 'bg-blue-500'
    },
    full: {
      badge: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
      dot: 'bg-red-500'
    },
    maintenance: {
      badge: 'bg-muted text-muted-foreground',
      dot: 'bg-gray-400'
    }
  };

  const barColor = getCapacityBarColor(shelf.utilization, shelf.status);

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-5'>
        <div className='mb-3 flex items-start justify-between gap-2'>
          <div className='min-w-0'>
            <p className='font-mono text-sm font-bold tracking-wider'>
              {shelf.shelf_code}
            </p>
            {shelf.shelf_type && (
              <p className='text-muted-foreground truncate text-xs'>
                {shelf.shelf_type}
              </p>
            )}
          </div>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
              statusVariants[shelf.status].badge
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                statusVariants[shelf.status].dot
              )}
            />
            {t(`status.${shelf.status}`)}
          </span>
        </div>

        <div className='text-muted-foreground mb-3 space-y-1 text-xs'>
          {shelf.storage_location && (
            <p>
              <span className='font-medium'>{t('columns.zone')}:</span>{' '}
              {shelf.storage_location.name}
            </p>
          )}
          {shelf.slot && (
            <p>
              <span className='font-medium'>{t('form.slot')}:</span>{' '}
              {shelf.slot}
            </p>
          )}
        </div>

        <div className='mb-4 space-y-1'>
          <div className='flex items-center justify-between text-xs'>
            <span className='text-muted-foreground'>
              {t('columns.currentCapacity')}
            </span>
            <span className='font-medium'>
              {shelf.current_capacity}
              <span className='text-muted-foreground font-normal'>
                /{shelf.max_capacity}
              </span>
            </span>
          </div>
          <div className='bg-muted h-1.5 w-full overflow-hidden rounded-full'>
            <div
              className={cn('h-full rounded-full transition-all', barColor)}
              style={{ width: `${Math.min(shelf.utilization, 100)}%` }}
            />
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={() => onEdit(shelf)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
          {shelf.status !== 'maintenance' && (
            <Button
              variant='ghost'
              size='icon'
              className='h-8 w-8'
              onClick={() => onMarkMaintenance(shelf)}
            >
              <Wrench className='h-3.5 w-3.5' />
            </Button>
          )}
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-8 w-8'
            onClick={() => onDelete(shelf)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
